import React from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";

import { cn } from "../../utils/lib";

export function DeleteCityIntent({ className }) {
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const dispatch = useDispatch();

  const authToken = useSelector(({ auth }) => auth.token);

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const currentSelectedCityName = useSelector(
    ({ pricing }) => pricing.cities[currentSelectedCityId]?.cityName
  );

  const [value, setValue] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isError, setIsError] = React.useState(false);

  const randomNumber = React.useMemo(
    () => Math.floor(Math.random() * 9000) + 1000,
    [currentSelectedCityId]
  );

  const deleteText = `${currentSelectedCityName}-${randomNumber}`;

  const handleDeleteCity = async () => {
    dispatch({
      type: "EXTRANET_DELETE_CITY",
      payload: {
        cityId: currentSelectedCityId,
        token: authToken,
        setErrorCallback: setIsError,
        setIsLoadingCallback: setIsLoading,
        onSuccessCallback: () => {
          setIsDialogOpen(false);
          setValue("");
        },
      },
    });
  };

  return (
    <AlertDialog open={isDialogOpen}>
      <AlertDialogTrigger asChild>
        <Button
          type="button"
          onClick={() => setIsDialogOpen(true)}
          className={cn(
            "w-full overflow-hidden border border-red-500 bg-red-50 px-4 text-red-500 hover:bg-red-100",
            className
          )}
        >
          <div className="block truncate">
            <span className="font-semibold">Delete</span>{" "}
            {currentSelectedCityName}
          </div>
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-black border border-[#353C4A]">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">
            Are you absolutely sure?
          </AlertDialogTitle>
          <AlertDialogDescription>
            <p className="mb-2">
              This action cannot be undone. This will permanently delete your
              pricing for{" "}
              <em className="inline-block font-medium not-italic text-white">
                {currentSelectedCityName}
              </em>{" "}
              and remove your data from our servers.
            </p>
            <Label>
              To Delete, type <span className="text-white">{deleteText}</span>
            </Label>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="focus-visible:ring-offset-0 bg-[#131821] text-[#4E5665] border border-[#353C4A] block w-full truncate focus:ring-0 placeholder:truncate focus:outline-none ring-transparent focus-visible:border disabled:border disabled:opacity-90 disabled:bg-[#131821] disabled:shadow-none focus-visible:border-transparent focus-visible:ring-none focus-visible:border-[#353C4A]"
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={isLoading}
            onClick={() => setIsDialogOpen(false)}
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="border border-red-500 bg-red-50 text-red-500 hover:bg-red-100 disabled:border-red-200"
            disabled={value !== deleteText || isLoading}
            onClick={handleDeleteCity}
          >
            {isError && <RefreshCcw className="text-red-500" />}
            {isLoading && <Loader2 className="animate-spin" />}
            <span>Delete</span>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
